import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Fab,
  FormControlLabel,
  Checkbox,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { makeStyles } from "@material-ui/core";
import ExpandMoreOutlinedIcon from '@material-ui/icons/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@material-ui/icons/ExpandLessOutlined';

const useStyles = makeStyles((theme) => ({
  label: {
    width: '100%',
    position: 'relative',
    '& span:nth-of-type(2)': {
      width: '100%',
    },
  },
}));

const QueueSelectConnection = ({
  selecteds = [],
  onSelected = () => { },
  isCollapsed = false,
  userId,
  filterQueue, // Filtro opcional (não define valor padrão para melhor performance)
  filterConnection, // Filtro opcional por conexão (ex.: WhatsApp oficial)
  ...props
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuWidth, setMenuWidth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [connections, setConnections] = useState([]);
  const [collapsedConnections, setCollapsedConnections] = useState([]);
  const [selectedConnectionOptions, setSelectedConnectionOptions] = useState([]);

  // Conexões filtradas (com ou sem filtro)
  const filteredConnections = useMemo(() => {
    let result = connections;
    if (filterConnection) {
      result = result.filter((conn) => filterConnection(conn));
    }
    if (!filterQueue) return result;
    return result.map(conn => ({
      ...conn,
      Queues: conn.Queues.filter(queue => filterQueue(queue))
    })).filter(conn => conn.Queues.length > 0);
  }, [connections, filterQueue, filterConnection]);

  // Seleções filtradas (com ou sem filtro)
  const filteredSelectedOptions = useMemo(() => {
    let result = selectedConnectionOptions;
    if (filterConnection) {
      result = result.filter((conn) => {
        const full = connections.find((c) => c.id == conn.connectionId);
        return full ? filterConnection(full) : false;
      });
    }
    if (!filterQueue) return result;
    return result
      .map(conn => ({
        ...conn,
        queueIds: conn.queueIds.filter(queueId => {
          const queue = connections
            .flatMap(c => c.Queues)
            .find(q => q.id === queueId);
          return queue && filterQueue(queue);
        })
      }))
      .filter(conn => conn.queueIds.length > 0);
  }, [selectedConnectionOptions, connections, filterQueue, filterConnection]);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchConnections = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/whatsappListAll", {
          params: { disabled: "0", userId },
          signal: abortController.signal,
        });

        if (!isMounted || !data) return;

        if (Array.isArray(data)) {
          const validConnections = data.filter(
            dt => !isNaN(+dt?.id) && dt?.Queues?.length > 0
          );
          setConnections(validConnections);
          if (isCollapsed) {
            setCollapsedConnections(validConnections.map(f => f.id));
          }
        }
      } catch (err) {
        if (!isMounted) return;
        console.error({ err });
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchConnections();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [isCollapsed, userId]);

  useEffect(() => {
    if (Array.isArray(selecteds) && selecteds.length > 0) {
      setSelectedConnectionOptions(selecteds);
    }
  }, [selecteds]);

  const handleClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + window.scrollY, left: rect.left });
    setMenuWidth(rect.width);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setMenuPosition({ top: 0, left: 0 });
    setMenuWidth(0);
    setAnchorEl(null);
    onSelected(filteredSelectedOptions);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase().trim());
  };

  const handleConnectionSelection = (connId) => {
    const conn = filteredConnections.find(c => c.id == connId);
    if (!conn) return;

    const connSelectedIdx = selectedConnectionOptions.findIndex(sc => sc.connectionId == connId);
    const allSelected = connSelectedIdx !== -1 &&
      selectedConnectionOptions[connSelectedIdx].queueIds.length === conn.Queues.length;

    const response = {
      connectionId: connId,
      queueIds: allSelected ? [] : conn.Queues.map(queue => queue.id)
    };

    setSelectedConnectionOptions(prev => {
      if (connSelectedIdx !== -1) {
        const updated = [...prev];
        updated[connSelectedIdx] = response;
        return updated;
      }
      return [...prev, response];
    });
  };

  const handleQueueSelection = (connectionId, queueId) => {
    setSelectedConnectionOptions(prev => {
      const index = prev.findIndex(option => option.connectionId === connectionId);

      if (index !== -1) {
        const current = prev[index];
        const queueAlreadySelected = current.queueIds.includes(queueId);

        const updatedQueueIds = queueAlreadySelected
          ? current.queueIds.filter(id => id !== queueId)
          : [...current.queueIds, queueId];

        const updated = [...prev];
        updated[index] = { connectionId, queueIds: updatedQueueIds };
        return updated;
      }

      return [...prev, { connectionId, queueIds: [queueId] }];
    });
  };

  const handleCollapsed = (connId) => {
    setCollapsedConnections(prev =>
      prev.includes(connId)
        ? prev.filter(cId => cId != connId)
        : [...prev, connId]
    );
  };

  const queueIsSelected = (connId, queueId) => {
    const selectedConnection = selectedConnectionOptions.find(c => c.connectionId == connId);
    return selectedConnection?.queueIds?.includes(queueId) ?? false;
  };

  const isAllQueueSelected = (connId) => {
    const selectedCount = selectedConnectionOptions.find(c => c.connectionId == connId)?.queueIds?.length || 0;
    const totalCount = filteredConnections.find(c => c.id == connId)?.Queues?.length || 0;
    return selectedCount === totalCount && totalCount > 0;
  };

  const isAllSelected = () => {
    let allSelected = true;
    let oneQueueSelected = false;

    filteredConnections.forEach(conn => {
      const selectedCount = selectedConnectionOptions.find(c => c.connectionId == conn.id)?.queueIds?.length || 0;
      const totalCount = conn.Queues.length;

      if (selectedCount < totalCount) allSelected = false;
      if (selectedCount > 0) oneQueueSelected = true;
    });

    return { allSelected, oneQueueSelected };
  };

  const allSelectedProps = isAllSelected();

  const handleSelectAllToggle = () => {
    if (allSelectedProps.allSelected) {
      setSelectedConnectionOptions(filteredConnections.map(conn => ({
        connectionId: conn.id,
        queueIds: []
      })));
    } else {
      setSelectedConnectionOptions(filteredConnections.map(conn => ({
        connectionId: conn.id,
        queueIds: conn.Queues.map(queue => queue.id)
      })));
    }
  };

  const inSearchParam = (connectionName, queueName) => {
    if (!searchTerm) return true;

    if (connectionName) {
      if (connectionName.toLowerCase().includes(searchTerm)) return true;

      if (Array.isArray(queueName)) {
        return queueName.some(q => q.toLowerCase().includes(searchTerm));
      }
      return false;
    }

    if (typeof queueName === "string") {
      return queueName.toLowerCase().includes(searchTerm);
    }

    return true;
  };

  return (
    <Box>
      <Button
        variant="outlined"
        fullWidth
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
        disabled={loading}
        style={{
          height: "40px",
          lineHeight: "40px",
          textTransform: "none"
        }}
        {...props}
      >
        Selecionar Conexões e Filas
      </Button>

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={{ top: menuPosition.top, left: menuPosition.left }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: "250px",
            width: menuWidth,
            boxShadow: "0px 5px 5px -3px rgba(0,0,0,0.2)",
            border: "1px solid rgba(0, 0, 0, 0.23)",
            borderTop: "none",
          },
        }}
      >
        <MenuItem
          dense
          disableRipple
          style={{
            width: "100%",
            height: "35px",
            lineHeight: "35px"
          }}
        >
          <TextField
            fullWidth
            placeholder="Pesquisar..."
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            InputProps={{
              style: { height: 25, padding: 0 },
              inputProps: {
                style: { height: 20, lineHeight: '20px', padding: '0 8px' },
              },
            }}
          />
        </MenuItem>

        {filteredConnections.length > 0 && (
          <>
            <MenuItem dense key="select-all" style={{ lineHeight: "35px", height: "35px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={allSelectedProps.allSelected}
                    indeterminate={!allSelectedProps.allSelected && allSelectedProps.oneQueueSelected}
                    onChange={handleSelectAllToggle}
                  />
                }
                label={<strong>{i18n.t("selectAll")}</strong>}
              />
            </MenuItem>

            {filteredConnections
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(conn => (
                <Box key={conn.id}>
                  <MenuItem
                    dense
                    hidden={!inSearchParam(conn.name, conn.Queues.map(q => q.name))}
                    style={{ lineHeight: "35px", height: "35px", position: 'relative' }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={isAllQueueSelected(conn.id)}
                          onChange={() => handleConnectionSelection(conn.id)}
                          indeterminate={
                            selectedConnectionOptions.some(c => c.connectionId === conn.id && c.queueIds.length > 0) &&
                            !isAllQueueSelected(conn.id)
                          }
                        />
                      }
                      className={classes.label}
                      label={
                        <Tooltip title={conn.name}>
                          <strong style={{
                            display: 'inline-block',
                            width: 'calc(100% - 50px)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            paddingTop: "5px"
                          }}>
                            {conn.name}
                          </strong>
                        </Tooltip>
                      }
                    />
                    <Fab
                      size="small"
                      style={{
                        position: "absolute",
                        right: "10px",
                        boxShadow: "none",
                        background: "transparent"
                      }}
                      onClick={() => handleCollapsed(conn.id)}
                    >
                      {collapsedConnections.includes(conn.id) ?
                        <ExpandMoreOutlinedIcon /> :
                        <ExpandLessOutlinedIcon />}
                    </Fab>
                  </MenuItem>

                  {conn.Queues
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(queue => (
                      <MenuItem
                        key={`${conn.id}-${queue.id}`}
                        dense
                        style={{
                          paddingLeft: "30px",
                          lineHeight: "35px",
                          height: "35px",
                          position: 'relative',
                        }}
                        hidden={collapsedConnections.includes(conn.id) || !inSearchParam(null, queue.name)}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              color="primary"
                              checked={queueIsSelected(conn.id, queue.id)}
                              onChange={() => handleQueueSelection(conn.id, queue.id)}
                            />
                          }
                          className={classes.label}
                          label={
                            <Tooltip title={queue.name}>
                              <Typography component="label" style={{
                                display: 'inline-block',
                                width: 'calc(100% - 30px)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                paddingTop: "12px"
                              }}>
                                {queue.name}
                              </Typography>
                            </Tooltip>
                          }
                        />
                      </MenuItem>
                    ))}
                </Box>
              ))}
          </>
        )}
      </Menu>
    </Box>
  );
};

export default QueueSelectConnection;